import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, Alert, Button, Drawer, Typography } from 'antd';
import { DownloadOutlined, EditOutlined } from '@ant-design/icons';
import PdfOverlayEditor from '../components/PdfOverlayEditor/PdfOverlayEditor';

const { Title, Text } = Typography;

const isPdfDocument = (meta) => {
  const format = String(meta?.outputFormat || '').toLowerCase();
  const mime = String(meta?.mimeType || '').toLowerCase();
  const fileName = String(meta?.fileName || '').toLowerCase();
  return format === 'pdf' || mime === 'application/pdf' || fileName.endsWith('.pdf');
};

const DocInstancePage = () => {
  const { id } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState('pdf');
  const [editorOpen, setEditorOpen] = useState(false);
  const [hasOverlay, setHasOverlay] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const opts = { headers, credentials: 'include' };
        const urls = [`/api/doc-instances/${id}`, `/api/DocInstances/${id}`];
        let data = null;
        let lastErr = null;
        for (const url of urls) {
          const res = await fetch(url, opts);
          const text = await res.text();
          const trimmed = text.trimStart();
          if (!res.ok) {
            lastErr = new Error(text || res.statusText);
            continue;
          }
          if (trimmed.startsWith('<')) {
            lastErr = new Error(
              '伺服器回傳網頁而非 JSON（API 路徑可能不符或尚未部署含 doc-instances 的後端）'
            );
            continue;
          }
          try {
            data = JSON.parse(text);
            break;
          } catch (e) {
            lastErr = e;
          }
        }
        if (!data) throw lastErr || new Error('無法載入文件資料');
        if (!cancelled) setMeta(data);
      } catch (e) {
        if (!cancelled) setError(e.message || '載入失敗');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Check overlay availability after meta is loaded
  useEffect(() => {
    if (!meta || !isPdfDocument(meta)) return;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`/api/doc-instances/${id}/overlay-edit`, { headers, credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.hasOverlay) setHasOverlay(true); })
      .catch(() => {});
  }, [id, meta]);

  useEffect(() => {
    if (!meta) return undefined;

    let cancelled = false;
    let objectUrl = '';
    (async () => {
      try {
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewUrl('');

        const token = localStorage.getItem('token');
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;

        const fetchPreviewBlob = async (urls) => {
          let lastError = null;
          for (const url of urls) {
            try {
              const res = await fetch(url, { headers, credentials: 'include' });
              const contentType = (res.headers.get('content-type') || '').toLowerCase();
              if (!res.ok) {
                lastError = new Error(await res.text() || res.statusText);
                continue;
              }
              if (contentType.includes('text/html') || contentType.includes('application/json')) {
                lastError = new Error(await res.text() || '伺服器回傳的不是可預覽檔案');
                continue;
              }
              return await res.blob();
            } catch (e) {
              lastError = e;
            }
          }
          throw lastError || new Error('載入預覽失敗');
        };

        // cache-bust after manual save (previewVersion > 0)
        const cb = previewVersion > 0 ? `?v=${previewVersion}&t=${Date.now()}` : '';

        const pdf = isPdfDocument(meta);
        setPreviewType(pdf ? 'pdf' : 'image');
        if (pdf) {
          const blob = await fetchPreviewBlob([
            `/api/doc-instances/${id}/preview${cb}`,
            `/api/DocInstances/${id}/preview${cb}`,
            `/api/doc-instances/${id}/file${cb}`,
            `/api/DocInstances/${id}/file${cb}`
          ]);
          const pdfBlob = blob.type === 'application/pdf'
            ? blob
            : new Blob([blob], { type: 'application/pdf' });
          objectUrl = URL.createObjectURL(pdfBlob);
          if (!cancelled) {
            setPreviewUrl(`${objectUrl}#toolbar=1&navpanes=0`);
          }
          return;
        }

        const blob = await fetchPreviewBlob([
          `/api/doc-instances/${id}/preview-image${cb}`,
          `/api/DocInstances/${id}/preview-image${cb}`
        ]);
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setPreviewUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setPreviewError(e.message || '載入預覽失敗');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, meta, previewVersion]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const fileUrls = [
        `/api/doc-instances/${id}/file`,
        `/api/DocInstances/${id}/file`
      ];
      let res = null;
      for (const url of fileUrls) {
        const r = await fetch(url, { headers, credentials: 'include' });
        const ct = (r.headers.get('content-type') || '').toLowerCase();
        if (r.ok && !ct.includes('text/html')) {
          res = r;
          break;
        }
        res = r;
      }
      if (!res.ok) throw new Error(await res.text() || res.statusText);
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (ct.includes('text/html')) {
        throw new Error('下載失敗：伺服器回傳網頁而非檔案');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = meta?.fileName || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || '下載失敗');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (error && !meta) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={error} />
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100vh',
        padding: 24,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        overflow: 'hidden'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ minWidth: 0 }}>
          <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>{meta?.instanceName || 'Document'}</Title>
          <Text type="secondary" ellipsis style={{ display: 'block' }}>{meta?.fileName}</Text>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {hasOverlay && (
            <Button icon={<EditOutlined />} onClick={() => setEditorOpen(true)}>
              編輯填表內容
            </Button>
          )}
          <Button type="primary" icon={<DownloadOutlined />} loading={downloading} onClick={handleDownload}>
            下載檔案
          </Button>
        </div>
      </div>

      <Drawer
        title="編輯填表內容"
        placement="right"
        width="100vw"
        styles={{ body: { padding: 0, overflow: 'hidden', height: '100%' } }}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        destroyOnClose
      >
        {editorOpen && (
          <PdfOverlayEditor
            docInstanceId={id}
            onSaveSuccess={() => {
              setEditorOpen(false);
              setPreviewVersion(v => v + 1);
            }}
            onClose={() => setEditorOpen(false)}
          />
        )}
      </Drawer>

      {error && <Alert type="warning" message={error} />}
      {previewError && <Alert type="warning" message="預覽未能載入" description={previewError} />}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          border: '1px solid #e8e8e8',
          borderRadius: 8,
          background: '#f7f7f7',
          overflow: previewType === 'pdf' ? 'hidden' : 'auto'
        }}
      >
        {previewLoading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: '#666' }}>載入預覽中...</div>
          </div>
        ) : previewUrl && previewType === 'pdf' ? (
          <iframe
            title={meta?.fileName || 'Document preview'}
            src={previewUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
              background: '#fff'
            }}
          />
        ) : previewUrl ? (
          <div style={{ padding: 16, textAlign: 'center', background: '#f7f7f7' }}>
            <img
              src={previewUrl}
              alt={meta?.fileName || 'Document first page preview'}
              style={{
                maxWidth: '100%',
                height: 'auto',
                background: '#fff',
                borderRadius: 6,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
              }}
            />
          </div>
        ) : (
          <div style={{ padding: 48, textAlign: 'center', color: '#999' }}>
            暫無預覽
          </div>
        )}
      </div>
    </div>
  );
};

export default DocInstancePage;
